import CryptoJS from "crypto-js";
import { setUserLogin } from "../store/action/Login";
import { resetStore } from "../store/action/Logout";
import history from "../history";

const originalFetch = window.fetch;

let isRefreshing = false;
let refreshSubscribers = [];
let appStore = null;

export const setInterceptorStore = (store) => {
  appStore = store;
};

function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}

function onRefreshed(token) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

const performLogout = () => {
  localStorage.removeItem("Token");
  localStorage.removeItem("RefreshToken");
  localStorage.removeItem("ID");
  localStorage.removeItem("password");
  localStorage.removeItem("email");
  localStorage.removeItem("hashPasswordFlag");
  sessionStorage.removeItem("path");
  localStorage.removeItem("userName");
  localStorage.removeItem("first_name");

  if (appStore) {
    appStore.dispatch(setUserLogin(false));
    appStore.dispatch(resetStore());
  }
  history.push("/");
};

const attemptTokenRefresh = async () => {
  const refreshToken = localStorage.getItem("RefreshToken");

  // Method 1: Try using refresh token if available
  if (refreshToken) {
    try {
      const refreshUrl = `${process.env.REACT_APP_DEV_PYTHON_URL}/account/refresh-token/`;
      const response = await originalFetch(refreshUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          refresh_token: refreshToken,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.access_token) {
          localStorage.setItem("Token", data.access_token);
          // If response contains a new refresh token, update it as well
          if (data.refresh_token) {
            localStorage.setItem("RefreshToken", data.refresh_token);
          } else if (data.refresh) {
            localStorage.setItem("RefreshToken", data.refresh);
          }
          return data.access_token;
        }
      }
    } catch (err) {
      console.error("Token refresh API failed:", err);
    }
  }

  // Method 2: Fall back to login credentials if refresh token fails or is not present
  const emailEnc = localStorage.getItem("email");
  const passwordEnc = localStorage.getItem("password");
  const hashFlag = localStorage.getItem("hashPasswordFlag");

  if (!emailEnc || !passwordEnc) {
    throw new Error("No credentials saved and refresh token failed");
  }

  let decryptedEmail = "";
  let decryptedPassword = "";

  try {
    if (hashFlag) {
      const emailBytes = CryptoJS.AES.decrypt(emailEnc, process.env.REACT_APP_SECRET_EMAIL_KEY);
      decryptedEmail = JSON.parse(emailBytes.toString(CryptoJS.enc.Utf8));

      const passwordBytes = CryptoJS.AES.decrypt(passwordEnc, process.env.REACT_APP_SECRET_PASSWORD_KEY);
      decryptedPassword = JSON.parse(passwordBytes.toString(CryptoJS.enc.Utf8));
    } else {
      decryptedEmail = emailEnc;
      decryptedPassword = passwordEnc;
    }
  } catch (err) {
    console.error("Failed to decrypt credentials during fallback refresh:", err);
    throw err;
  }

  const loginResponse = await originalFetch(
    `${process.env.REACT_APP_DEV_PYTHON_URL}/account/login-pnd/`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        username: decryptedEmail,
        password: decryptedPassword,
      }),
    }
  );

  if (!loginResponse.ok) {
    throw new Error("Failed to login during fallback token refresh");
  }

  const data = await loginResponse.json();
  if (data && data.token) {
    localStorage.setItem("Token", data.token);
    localStorage.setItem("ID", data.user.id);
    localStorage.setItem("first_name", data.user.first_name);
    if (data.refresh_token) {
      localStorage.setItem("RefreshToken", data.refresh_token);
    } else if (data.refresh) {
      localStorage.setItem("RefreshToken", data.refresh);
    }
    return data.token;
  } else {
    throw new Error("Invalid credentials or response during fallback token refresh");
  }
};

window.fetch = async function (...args) {
  let [resource, config] = args;

  // Clone config to ensure we can modify headers safely
  config = config ? { ...config } : {};

  // If there's an active token, ensure it is set correctly in headers
  // (This handles case where token was updated by a concurrent refresh)
  if (config.headers) {
    const token = localStorage.getItem("Token");
    if (token) {
      if (config.headers instanceof Headers) {
        if (config.headers.has("Authorization")) {
          config.headers.set("Authorization", "Token " + token);
        }
      } else if (typeof config.headers === "object") {
        const authKey = Object.keys(config.headers).find(
          (k) => k.toLowerCase() === "authorization"
        );
        if (authKey) {
          config.headers[authKey] = "Token " + token;
        }
      }
    }
  }

  try {
    const response = await originalFetch(resource, config);

    // If unauthorized, attempt refresh
    if (response.status === 401) {
      const isLoginUrl = typeof resource === "string" && resource.includes("/account/login-pnd");
      const isRefreshUrl = typeof resource === "string" && resource.includes("/account/refresh-token");

      if (isLoginUrl || isRefreshUrl) {
        return response;
      }

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await attemptTokenRefresh();
          isRefreshing = false;
          onRefreshed(newToken);
        } catch (error) {
          isRefreshing = false;
          performLogout();
          return response;
        }
      }

      // Return a promise that resolves with the retried request once refresh completes
      return new Promise((resolve) => {
        subscribeTokenRefresh((newToken) => {
          if (config.headers) {
            if (config.headers instanceof Headers) {
              config.headers.set("Authorization", "Token " + newToken);
            } else if (typeof config.headers === "object") {
              const authKey = Object.keys(config.headers).find(
                (k) => k.toLowerCase() === "authorization"
              );
              if (authKey) {
                config.headers[authKey] = "Token " + newToken;
              }
            }
          }
          resolve(originalFetch(resource, config));
        });
      });
    }

    return response;
  } catch (error) {
    throw error;
  }
};
